import { autoinject } from 'aurelia-framework';
import { ValidationController, Validator } from 'aurelia-validation';
import { I18N } from 'aurelia-i18n';
import { AquacomComponentProduct, MooringArticleCategory, MooringArticleType, MooringProductType } from 'models/mooring';
import { MooringArticleCategoryService, MooringArticleTypeService, MooringProductTypeService, ToastService } from 'services';
import { DialogController } from 'aurelia-dialog';
import { StandardValidationBuilder } from 'lib/validation';
import { AsyncErrorHandler } from 'lib/ui';

@autoinject
export class MooringProductTypeCreateEdit {
  ready = false;

  protected categories: MooringArticleCategory[] = [];
  protected categoryId: number;
  protected articleTypes: MooringArticleType[] = [];
  protected aquacomProducts: (AquacomComponentProduct & { PathName: string })[] = [];

  protected form: MooringProductType = {
    Id: undefined,
    Name: undefined,
    NameEn: undefined,
    NameEs: undefined,
    ArticleTypeId: undefined,
  };

  private typeLocked: boolean = false;

  constructor(
    protected validator: Validator,
    private validationController: ValidationController,
    private mooringCategoryService: MooringArticleCategoryService,
    private mooringArticleTypeService: MooringArticleTypeService,
    private mooringProductTypeService: MooringProductTypeService,
    private t: I18N,
    private toast: ToastService,
    private dialog: DialogController,
    private validationBuilder: StandardValidationBuilder
  ) {}

  protected applyValidationRules() {
    this.validationBuilder.with(this.form).required('Name').required('NameEn').required('ArticleTypeId').done();
  }

  protected activate(params?: { productId?: number; typeId?: number; aquacomTypeId?: number; categoryId?: number }) {
    void this.setup(params?.productId, params?.typeId, params?.aquacomTypeId, params?.categoryId);
  }

  protected async setup(productId?: number, typeId?: number, aquacomTypeId?: number, categoryId?: number) {
    await this.getCategories();
    await this.getArticleTypes();
    await this.getAquacomProductTypes(aquacomTypeId);

    if (productId) {
      this.getProductType(productId);
    } else {
      this.form.ArticleTypeId = typeId;
      this.categoryId = categoryId;
      this.typeLocked = true;
    }

    this.applyValidationRules();
    this.ready = true;
  }

  @AsyncErrorHandler
  protected async getProductType(productId: number) {
    const theType = await this.mooringProductTypeService.get(productId);
    this.form = {
      ...theType,
    };
  }

  @AsyncErrorHandler
  protected async getAquacomProductTypes(aquacomTypeId: number) {
    const p = await this.mooringProductTypeService.getAquacomTypes(aquacomTypeId);
    this.aquacomProducts = p.map((x) => ({
      ...x,
      PathName: `${x.CategoryName} - ${x.TypeName} - ${x.Name}`,
    }));
  }

  @AsyncErrorHandler
  async getCategories() {
    this.categories = await this.mooringCategoryService.getAll();
  }

  @AsyncErrorHandler
  protected async getArticleTypes() {
    this.articleTypes = await this.mooringArticleTypeService.getAll();
  }

  @AsyncErrorHandler
  protected async onSave(productType: MooringProductType) {
    if (productType.Id) {
      delete productType.ArticleType;

      await this.mooringProductTypeService.put(productType, productType.Id);
    } else {
      await this.mooringProductTypeService.post(productType);
    }
    this.toast.showSuccess(this.t.tr('general.saved'));
    this.dialog.close(true);
  }

  @AsyncErrorHandler
  async save() {
    const { valid } = await this.validationController.validate();
    if (!valid) return;
    this.onSave?.(this.form);
  }

  @AsyncErrorHandler
  protected async onDelete() {
    if (!this.form.Id) return;

    await this.mooringProductTypeService.delete(this.form.Id);
    this.toast.showSuccess(this.t.tr('general.deleted'));
    this.cancel();
  }

  cancel() {
    this.dialog.close(false);
  }
}
